import { Input, InputAdornment, Option, Options, Popover, Text, theme, SpinnerContainer } from '@alefeducation/ui-elements-library-core'
import { Check, TimesCircle } from '@alefeducation/ui-elements-library-icons'
import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import useOutsideClickListener from '../../DatePicker/useOutsideClickListener'
import { WithCommonType } from '../types'
import fetchUsers, { userResponse, usersResponse } from '../../../xhr/getUsers'
import getUsersByUUIDs from '../../../xhr/getUsersByUUIDs'
import { debounce } from '../../../utils/debounce'
import { handleUserSelectionClick } from '../../../helper/userSelection'
import { SelectOptionMessage } from '../../common/SelectOptionMessage'

const StyledInput = styled(Input)`
  border: none;
  outline: none;
  box-shadow: none;
`

const Wrapper = styled.div`
  background-color: ${theme.colors.white};
  border-radius: 8px;
  border: 1px solid ${theme.colors.primary[40]};
  .selected-user-list {
    &:empty {
      display: none;
    }
    display: flex;
    padding: 10px 12px;
    gap: 6px;
    > div {
      display: flex;
      gap: 8px;
      color: ${theme.colors.primary[40]};
      border-radius: 100px;
      background: ${theme.colors.primary[10]};
      padding: 4px 8px;
      align-items: center;
    }
  }
  #user-filter-dropdown {
    width: 272px;
    > div.options {
      max-height: 100vh;
      height: auto;
    }
    .option-content {
      display: flex;
      justify-content: space-between;
      align-items: center;
      text-transform: capitalize;
    }
    ul > li > div > div {
      display: block;
    }
    .empty-user-message {
      text-align: center;
      color: ${theme.colors.primary[40]};
    }
  }
`

export type UserSelectorTemplate = {
  selectedIds?: { id?: string; uuid?: string }[]
}

type UserSelectorProps = WithCommonType<UserSelectorTemplate>

const UsersSelectorTemplate = (props: UserSelectorProps) => {
  const { onChange, selectedIds, name, index } = props

  const [popoverRef, setPopoverRef] = useState<null | HTMLDivElement>(null)
  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false)
  const [selected, setSelected] = useState<UserSelectorProps['selectedIds']>(selectedIds)
  const [term, setTerm] = useState<string>(''),
    [typeahead, setTypeahead] = useState<string>('')
  const [users, setUsers] = useState<usersResponse['users']>([])
  const [selectedUserList, setSelectedUserList] = useState<userResponse[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string>('')
  useOutsideClickListener([popoverRef], () => setIsFilterOpen(false))

  useEffect(() => {
    onChange({ name, index, selectedIds: selected })
  }, [selected])

  useEffect(() => {
    setIsFilterOpen(!!term)
  }, [term])

  useEffect(() => {
    async function getUsers() {
      setLoading(true)
      setError('')
      try {
        const result = await fetchUsers({ search: term.trim() })
        if (result && result.users) {
          if (result.users.length === 0) {
            setUsers([])
          } else {
            setUsers(result.users)
          }
        } else {
          setError('Something went wrong')
          setUsers([])
        }
      } catch (error) {
        setError('Something went wrong.')
        setUsers([])
      } finally {
        setLoading(false)
      }
    }
    if (term.trim()) getUsers()
  }, [term])

  useEffect(() => {
    async function getUsersByIds() {
      const result = await getUsersByUUIDs(selected.map(s => s.uuid))
      setSelectedUserList(result)
    }
    if (props.selectedIds.length === 0 && selected.length > 0) setSelected([])
    if (selected.length > 0) getUsersByIds()
  }, [props.selectedIds])

  const onTypeAhead = useCallback(
    debounce(value => {
      ;(value.trim() === '' || value.length > 2) && setTerm(value)
    }, 400),
    []
  )

  return (
    <Wrapper>
      <div className="selected-user-list">
        {selectedIds.map(selected => (
          <div key={`selected-name-${selected}`}>
            <Text variant="body-medium" weight="regular" data-testid={`users-template-selected-user-${selected.uuid}`}>
              {selectedUserList.find(u => u.uuid.toString() === selected.uuid)?.name}
            </Text>
            <TimesCircle
              fontSize={12}
              color="primary.100"
              data-testid={`users-template-remove-selected-user-${selected.uuid}`}
              onClick={() => setSelected(prev => prev.filter(f => f !== selected))}
            />
          </div>
        ))}
      </div>
      <StyledInput
        id="search_input"
        data-testid="users-template-search-input"
        alt="input-search"
        placeholder="Search users"
        ref={setPopoverRef}
        value={typeahead}
        key={name + index}
        onFocus={() => {
          setIsFilterOpen(!!term)
        }}
        onChange={({ target: { value } }) => {
          onTypeAhead(value)
          setTypeahead(value)
        }}
        endAdornment={
          term ? (
            <InputAdornment>
              <TimesCircle fontSize={14} color="primary.100" onClick={() => setTerm('')} />
            </InputAdornment>
          ) : null
        }
        autoComplete="one-time-code"
      />

      {popoverRef ? (
        <Popover
          data-testid="users-template-users-list"
          id="user-filter-dropdown"
          style={{
            width: popoverRef.clientWidth
          }}
          anchorRef={popoverRef}
          isOpen={isFilterOpen}
          popoverPosition="bottom"
          setIsOpen={setIsFilterOpen}
        >
          <Options>
            {loading
              ? [<SelectOptionMessage type="loading" key="loading" />]
              : error
              ? [<SelectOptionMessage type="error" key="error" />]
              : users.length === 0
              ? [<SelectOptionMessage type="empty" key="empty" />]
              : users.map(user => (
                  <Option
                    key={user.id}
                    onClick={() =>
                      handleUserSelectionClick(user, selected, setSelected, selectedUserList, setSelectedUserList, setTerm, setTypeahead)
                    }
                    label={user.name}
                    data-testid={`users-filter-option-${user.id}`}
                  >
                    <div className="option-content">
                      <span>{user.name}</span>
                      {selectedIds.some(selected => selected.id === user.id.toString()) ? (
                        <Check className="check-tick" color={theme.colors.success['100']} />
                      ) : null}
                    </div>
                  </Option>
                ))}
          </Options>
        </Popover>
      ) : null}
    </Wrapper>
  )
}

export default UsersSelectorTemplate
